import {
    BasePath,
    Socials,
    SupportedChains,
    SupportedConnectors,
    Tokens,
} from '../../config/constants';

// socials
import Discord from './social/Discord';
import Telegram from './social/Telegram';
import Twitter from './social/Twitter';
import Medium from './social/Medium';
import Gitbook from './social/Gitbook';
import LinkedIn from './social/LinkedIn';

// connectors
import Metamask from './connectors/Metamask';
import WalletConnect from './connectors/WalletConnect';
import Coinbase from './connectors/Coinbase';
import Trezor from './connectors/Trezor';
import Portis from './connectors/Portis';
import Torus from './connectors/Torus';
// import Ledger from './connectors/Ledger';
// import Fortmatic from './connectors/Fortmatic';

// chain
import Polygon from './chain/Polygon';
import BinanceSmartChain from './chain/BinanceSmartChain';
import Arbitrum from './chain/Arbitrum';
import Development from './chain/Development';

// routes
import Home from './routes/Home';
import Pools from './routes/Pools';
import Trade from './routes/Trade';
import Analytics from './routes/Analytics';
import Leaderboard from './routes/Leaderboard';
import Referrals from './routes/Referrals';

// tokens
import ApeCoin from './tokens/ApeCoin';
import Avalanche from './tokens/Avalanche';
import Bitcoin from './tokens/Bitcoin';
import Bnb from './tokens/Bnb';
import Cardano from './tokens/Cardano';
import Ether from './tokens/Ether';
import Matic from './tokens/Polygon'; // Imported as token name to avoid conflict with Polygon chain
import ShibaInu from './tokens/ShibaInu';
import Uniswap from './tokens/Uniswap';
import UsdCoin from './tokens/UsdCoin';
import Xrp from './tokens/Xrp';
import Fallback from './tokens/Fallback';
import ChainLink from './tokens/ChainLink';
import Bluechip from './pools/Bluechip';
import Altcoin from './pools/Altcoin';
import Degen from './pools/Degen';
import Polkadot from './tokens/Polkadot';
import Dogecoin from './tokens/Dogecoin';
import ArbitrumToken from './tokens/Arbitrum';

// pools
export { default as BluechipIcon } from './pools/Bluechip';
export { default as AltcoinIcon } from './pools/Altcoin';
export { default as DegenIcon } from './pools/Degen';

// modal
export { default as CrossIcon } from './modal/Cross';
export { default as ErrorIcon } from './modal/Error';
export { default as SuccessIcon } from './modal/Success';
export { default as WarningIcon } from './modal/Warning';
export { default as SpinnerIcon } from './modal/Spinner';

// general
export { default as InfoRoundedIcon } from './general/InfoRounded';
export { default as WalletIcon } from './general/Wallet';
export { default as ChevronIcon } from './general/Chevron';
export { default as TotalValueLockedIcon } from './general/TotalValueLocked';
export { default as TotalTradingVolumeIcon } from './general/TotalTradingVolume';
export { default as TotalProtocolFeesIcon } from './general/TotalProtocolFees';
export { default as FriendsIcon } from './general/Friends';
export { default as RewardsIcon } from './general/Rewards';
export { default as LinkIcon } from './general/Link';
export { default as IncreasedArrowIcon } from './general/IncreasedArrow';
export { default as DecreasedArrowIcon } from './general/DecreasedArrow';
export { default as CrownIcon } from './general/Crown';
export { default as IndicatorPointerIcon } from './general/IndicatorPointer';
export { default as EditIcon } from './general/Edit';
export { default as CopyIcon } from './general/Copy';
export { default as SettingsIcon } from './general/Settings';
export { default as PlusIcon } from './general/Plus';
export { default as MinusIcon } from './general/Minus';
export { default as ShareIcon } from './general/Share';
export { default as ArrowRightRoundedIcon } from './general/ArrowRightRounded';
export { default as ArrowRightIcon } from './general/ArrowRight';
export { default as SearchIcon } from './general/Search';
export { default as StarIcon } from './general/Star';
export { default as CalendarIcon } from './general/Calendar';
export { default as DownloadIcon } from './general/Download';
export { default as TweetIcon } from './general/Tweet';
export { default as ReferralFeeIcon } from './general/ReferralFee';
export { default as QuestionMarkIcon } from './general/QuestionMark';
export { default as TxLinkIcon } from './general/TxLink';
export { default as DisconnectIcon } from './general/Disconnect';
export { default as TransactionSpinnerIcon } from './general/TransactionSpinner';
export { default as LockedIcon } from './general/Locked';
export { default as UnlockedIcon } from './general/Unlocked';
export { default as ConversionArrowIcon } from './general/ConversionArrow';

export type Icons<T extends string | number> = Partial<Record<T, JSX.Element>>;

export const SocialIcons: Icons<Socials> = {
    [Socials.DISCORD]: Discord(),
    [Socials.TELEGRAM]: Telegram(),
    [Socials.TWITTER]: Twitter(),
    [Socials.MEDIUM]: Medium(),
    [Socials.GITBOOK]: Gitbook(),
    [Socials.LINKEDIN]: LinkedIn(),
};

export const RouteIcons: Icons<BasePath> = {
    [BasePath.HOME]: Home(),
    [BasePath.POOLS]: Pools(),
    [BasePath.TRADE]: Trade(),
    [BasePath.ANALYTICS]: Analytics(),
    [BasePath.LEADERBOARD]: Leaderboard(),
    [BasePath.REFERRALS]: Referrals(),
};

export const TokenIcons: Icons<Tokens> = {
    [Tokens.APE]: ApeCoin(),
    [Tokens.AVAX]: Avalanche(),
    [Tokens.BTC]: Bitcoin(),
    [Tokens.BNB]: Bnb(),
    [Tokens.ADA]: Cardano(),
    [Tokens.ETH]: Ether(),
    [Tokens.GOERLI_ETH]: Ether(),
    [Tokens.MATIC]: Matic(),
    [Tokens.SHIB]: ShibaInu(),
    [Tokens.UNI]: Uniswap(),
    [Tokens.USDC]: UsdCoin(),
    [Tokens.XRP]: Xrp(),
    [Tokens.LINK]: ChainLink(),
    [Tokens.BLUECHIP]: Bluechip(),
    [Tokens.ALTCOIN]: Altcoin(),
    [Tokens.DEGEN]: Degen(),
    [Tokens.DOT]: Polkadot(),
    [Tokens.DOGE]: Dogecoin(),
    [Tokens.ARB]: ArbitrumToken(),
    [Tokens.FALLBACK]: Fallback(),
};

export const WalletConnectorIcons: Icons<SupportedConnectors> = {
    [SupportedConnectors.METAMASK]: Metamask(),
    [SupportedConnectors.WALLET_CONNECT]: WalletConnect(),
    [SupportedConnectors.COINBASE]: Coinbase(),
    [SupportedConnectors.TREZOR]: Trezor(),
    [SupportedConnectors.PORTIS]: Portis(),
    // [SupportedConnectors.LEDGER]: Ledger(),
    [SupportedConnectors.TORUS]: Torus(),
    // [SupportedConnectors.FORTMATIC]: Fortmatic(),
};

export const ChainIcons: Icons<SupportedChains> = {
    [SupportedChains.DEV]: Development(),
    [SupportedChains.POLYGON]: Polygon(),
    [SupportedChains.ARBITRUM_ONE]: Arbitrum(),
    [SupportedChains.BINANCE_SMART_CHAIN]: BinanceSmartChain(),
};
